/**
 * The marks the booking screen draws that no shared glyph draws as the canon does: the two on a
 * session block's pill, and three of the session panel's action rows (T-A5).
 *
 * - `TrainingGlyph` — the A1 canon's HORIZONTAL dumbbell (`T-A1-kalendarz.html`, `.mk svg`): two
 *   plates each side of a bar. The registry's `dumbbell` (mobile's, and `DumbbellMarkIcon` here) is
 *   drawn on the diagonal, and at the pill's 10px a diagonal bar across a round pill reads as a
 *   "no entry" sign. The canon is the visual contract for this surface, so its drawing is used.
 * - `ClientGlyph` — mobile's `user` glyph exactly (`Icon.glyphs.tsx`), the person the pill adds
 *   when the client booked. `SoloIcon` is a different, smaller person drawn for another meaning.
 *
 * The consultation's camera is the shared `ConsultationIcon`, which already is mobile's `video`.
 * Every mark is `aria-hidden`: the block says all of it in words.
 */

interface GlyphProps {
  readonly className?: string;
}

const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export function TrainingGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M6.5 6.5v11M17.5 6.5v11M3.5 9v6M20.5 9v6M6.5 12h11" />
    </svg>
  );
}

export function ClientGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="8" r="4" />
      <path d="M5.5 21a7.5 7.5 0 0 1 13 0" />
    </svg>
  );
}

/** The day's plan beside the grid: a page with its right-hand column ruled off. */
export function DayPlanGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="M15 4.5v15" />
    </svg>
  );
}

/** "Odwołaj" — a circle struck through, the canon's call-off mark (T-A5). */
export function BanGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6l12.8 12.8" />
    </svg>
  );
}

/** "Anuluj obciążenie" — the arrow turning back (T-A5). */
export function UndoGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </svg>
  );
}

/** "Przejrzyj sesję" — the lines of a session's record (T-A5). */
export function ListGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

/** "Napisz" — a speech bubble, drawn as the navigation's Czat mark is (`app/ui/NavIcons`). */
export function MessageGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M20.5 11.4c0 4-3.8 7.1-8.5 7.1-1.2 0-2.3-.2-3.3-.6L4 19.5l1.4-3.7a6.5 6.5 0 0 1-1.9-4.4c0-4 3.8-7.1 8.5-7.1s8.5 3.1 8.5 7.1Z" />
    </svg>
  );
}
